/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slide,
} from "@mui/material";
import { TransitionProps } from "notistack";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../context/notification-provider";
import LoadingScreen from "../progress/loading-screen";
import { WatchListSearchResult } from "./types";
import useWatchListType from "./use-watch-list";
import { addEntityToWatchList } from "./watch-list-service";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AddWatchlistDialog = ({
  selectedEntity,
  open,
  onClose,
}: {
  selectedEntity: WatchListSearchResult;
  open: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [watchListTypeId, setWatchListTypeId] = React.useState<string>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data, isLoading, error } = useWatchListType();

  const { showNotification } = useNotification();

  const handleFormSubmit = React.useCallback(async () => {
    setIsSubmitting(true);
    try {
      await addEntityToWatchList({
        ...selectedEntity,
        watchlistTypeId: Number(watchListTypeId),
        id: 0,
      });
      showNotification({
        message: "Entity added to watch list successfully.",
        severity: "success",
        duration: 3000,
      });
    } catch (err) {
      showNotification({
        message: `Failed to add entity to watch list. Reason: ${(err as Error).message}`,
        severity: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
      onClose();
      navigate(`/watch-list?watchlist=${watchListTypeId}`);
    }
  }, [navigate, onClose, selectedEntity, showNotification, watchListTypeId]);

  const handleOnWatchListTypeChange = (event: any) => {
    setWatchListTypeId(event.target.value);
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="md"
      fullWidth
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}>
        Add to Watchlist
      </DialogTitle>
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <DialogContent sx={{ pb: 0 }}>
          {error ? (
            <Alert severity="error">Unable to load watchlist types.</Alert>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                Are you sure you want to add the entity{" "}
                <b>{selectedEntity.description}</b> to a watchlist?
              </Box>
              <Box sx={{ mb: 2 }}>
                Please select the <b>watch list type</b> from the dropdown
                below.
              </Box>
              {data && !error && (
                <FormControl sx={{ width: "50%", mb: 2 }} size="small">
                  <InputLabel id="watch-list-type-label">
                    Watch list type
                  </InputLabel>
                  <Select
                    labelId="watch-list-type-label"
                    id="watch-list-type-select"
                    label="Watch list type"
                    value={watchListTypeId || ""}
                    onChange={handleOnWatchListTypeChange}
                  >
                    {data?.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.description}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleFormSubmit}
          loading={isSubmitting}
          disabled={!watchListTypeId || isSubmitting}
        >
          Add to watchlist
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default AddWatchlistDialog;
